import React, { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, CheckCircle2, AlertTriangle, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  business_name: "",
  business_type: "",
  monthly_volume: "",
  website: "",
  current_processor: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus({
        state: "success",
        msg: "Submission received. A UMG payments specialist will reach out within 1 business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setStatus({
        state: "error",
        msg:
          typeof detail === "string"
            ? detail
            : "Please check your inputs and try again.",
      });
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative border-b border-ink-900 bg-white" data-testid="contact-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid opacity-50" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 bg-accent" />
              <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500">
                /// File 05 — Contact UMG
              </span>
            </div>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-ink-900 sm:text-7xl lg:text-8xl">
              Claim your <span className="text-accent">free</span>
              <br />
              consultation
              <br />
              <span className="text-outline">today.</span>
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-sm leading-relaxed text-ink-500">
              Ready to transform your payment processing? Submit the form and a
              specialist will deliver a custom, no-obligation quote.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="border-b border-ink-900 bg-white" data-testid="contact-form-section">
        <div className="container-wide grid gap-0 lg:grid-cols-12">
          {/* LEFT: contact info */}
          <aside className="border-ink-900 lg:col-span-4 lg:border-r lg:p-12 p-8">
            <span className="eyebrow-accent">
              <span className="h-2 w-2 bg-accent" /> Direct Channels
            </span>
            <h2 className="mt-6 font-display text-3xl font-black uppercase leading-tight tracking-tighter sm:text-4xl">
              We're here
              <br />
              to help.
            </h2>
            <p className="mt-6 font-mono text-sm leading-relaxed text-ink-500">
              Questions about merchant accounts, compliance, or alternative
              payment methods — reach out directly or use the form.
            </p>

            <ul className="mt-10 space-y-6 border-t border-ink-900 pt-8">
              <ContactRow icon={Mail} label="Email" value="info@umg.inc" href="mailto:info@umg.inc" testid="contact-email" />
              <ContactRow icon={Phone} label="Support" value="Dedicated Merchant Assistance" testid="contact-phone" />
            </ul>

            <div className="mt-10 border border-ink-900 p-6">
              <h3 className="font-display text-lg font-black uppercase tracking-tighter">
                What happens next?
              </h3>
              <ol className="mt-5 space-y-4">
                {[
                  "Review of your submission within 1 business day.",
                  "Discovery call with a payments specialist.",
                  "Custom quote and onboarding kickoff.",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center border border-ink-900 bg-accent font-mono text-[11px] font-semibold text-white">
                      0{i + 1}
                    </span>
                    <span className="font-mono text-sm leading-relaxed text-ink-700">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* RIGHT: form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-8 p-8 lg:p-12"
            data-testid="contact-form"
          >
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-900 pb-6">
              <div>
                <span className="eyebrow-accent">
                  <span className="h-2 w-2 bg-accent" /> Request a Free Quote
                </span>
                <h2 className="mt-4 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">
                  Tell us about your business
                </h2>
              </div>
              <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-400">
                Form // 2026.01
              </span>
            </div>

            <div className="mt-10 grid gap-8 sm:grid-cols-2">
              <Field label="First Name" required n="01">
                <input
                  name="first_name"
                  required
                  value={form.first_name}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="Jane"
                  data-testid="contact-first-name"
                />
              </Field>
              <Field label="Last Name" required n="02">
                <input
                  name="last_name"
                  required
                  value={form.last_name}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="Doe"
                  data-testid="contact-last-name"
                />
              </Field>
              <Field label="Email" required n="03">
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="jane@company.com"
                  data-testid="contact-email-input"
                />
              </Field>
              <Field label="Phone" required n="04">
                <input
                  name="phone"
                  required
                  value={form.phone}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="+1 ..."
                  data-testid="contact-phone-input"
                />
              </Field>
              <Field label="Business Name" required n="05">
                <input
                  name="business_name"
                  required
                  value={form.business_name}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="Acme Inc."
                  data-testid="contact-business-name"
                />
              </Field>
              <Field label="Business Type" required n="06">
                <select
                  name="business_type"
                  required
                  value={form.business_type}
                  onChange={onChange}
                  className="input-brutal"
                  data-testid="contact-business-type"
                >
                  <option value="">Select type…</option>
                  <option>Retail</option>
                  <option>E-Commerce</option>
                  <option>B2B</option>
                  <option>Startup / SaaS</option>
                  <option>TeleMed</option>
                  <option>Peptides</option>
                  <option>Coach / Consultant</option>
                  <option>Creator / Publisher</option>
                  <option>Auto Dealership</option>
                  <option>High-Risk (CBD, Peptides, etc.)</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Monthly Volume" n="07">
                <select
                  name="monthly_volume"
                  value={form.monthly_volume}
                  onChange={onChange}
                  className="input-brutal"
                  data-testid="contact-monthly-volume"
                >
                  <option value="">Select range…</option>
                  <option>Under $10,000</option>
                  <option>$10,000 – $25,000</option>
                  <option>$25,000 – $50,000</option>
                  <option>$50,000 – $100,000</option>
                  <option>$100,000 – $250,000</option>
                  <option>$250,000+</option>
                </select>
              </Field>
              <Field label="Website" n="08">
                <input
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  className="input-brutal"
                  placeholder="https://"
                  data-testid="contact-website"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Current Processor" n="09">
                  <input
                    name="current_processor"
                    value={form.current_processor}
                    onChange={onChange}
                    className="input-brutal"
                    placeholder="e.g. Stripe, Square, or N/A"
                    data-testid="contact-current-processor"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Additional Information" n="10">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    className="input-brutal resize-y"
                    placeholder="Tell us about your business, pain points, and goals…"
                    data-testid="contact-message"
                  />
                </Field>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-ink-900 pt-8">
              <p className="max-w-md font-mono text-[11px] uppercase tracking-wide2 text-ink-400">
                By submitting, you agree to be contacted about UMG services. We never share your information.
              </p>
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="btn-primary"
                data-testid="contact-submit"
              >
                {status.state === "loading" ? "Sending…" : "Submit Request"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {status.state === "success" && (
              <div
                className="mt-8 flex items-start gap-3 border border-ink-900 bg-ink-50 p-5"
                data-testid="contact-success"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-accent" />
                <span className="font-mono text-sm text-ink-900">{status.msg}</span>
              </div>
            )}
            {status.state === "error" && (
              <div
                className="mt-8 flex items-start gap-3 border border-accent bg-white p-5"
                data-testid="contact-error"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 text-accent" />
                <span className="font-mono text-sm text-accent">{status.msg}</span>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, n, children }) {
  return (
    <label className="block">
      <div className="mb-2 flex items-center justify-between">
        <span className="label-brutal">
          {label}
          {required && <span className="text-accent"> *</span>}
        </span>
        {n && (
          <span className="font-mono text-[10px] tracking-ultra text-ink-400">{n}</span>
        )}
      </div>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <Icon className="mt-1 h-4 w-4 text-accent" />
      <div>
        <div className="font-mono text-[11px] uppercase tracking-ultra text-ink-400">
          {label}
        </div>
        <div className="mt-1 font-mono text-sm text-ink-900">{value}</div>
      </div>
    </div>
  );
  return (
    <li data-testid={testid}>
      {href ? (
        <a href={href} className="block hover:text-accent">
          {inner}
        </a>
      ) : (
        inner
      )}
    </li>
  );
}
